import type { SubscriptionTypes } from "@component/Widget/NewsletterSubscribe/NewsletterSubscribe";
import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormFields, LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import type { SmeLoanLeadCalcRequest, SmeLoanLeadCalcResponse } from "./types";

export interface SmeLoanLeadFormFields extends AbstractLeadFormFields {
	companyName: string;
	companyFoundYear?: number;
	netIncome?: number;
	operatingProfit?: number;
	equity?: number;
	loanAmount?: number;
}

export type SmeLoanLeadFormProps<
	LeadCalculatorRequest extends SmeLoanLeadCalcRequest = SmeLoanLeadCalcRequest,
	LeadCalculatorResponse extends SmeLoanLeadCalcResponse = SmeLoanLeadCalcResponse,
> = AbstractLeadFormProps<LeadCalculatorRequest, LeadCalculatorResponse>;

export interface SmeLoanLeadFormState<FormFields extends SmeLoanLeadFormFields = SmeLoanLeadFormFields>
	extends AbstractLeadFormState<FormFields> {
	errorMessage?: string;
}

/**
 * @Shortcode
 *
 * @title "Vállalati hitel leadform"
 * @shortcode "sme_loan_lead"
 * @slug "vallalati-hitel"
 * @type "leadform"
 */
export class SmeLoanLeadForm<
	LeadCalculatorRequest extends SmeLoanLeadCalcRequest = SmeLoanLeadCalcRequest,
	LeadCalculatorResponse extends SmeLoanLeadCalcResponse = SmeLoanLeadCalcResponse,
	FormFields extends SmeLoanLeadFormFields = SmeLoanLeadFormFields,
	Props extends SmeLoanLeadFormProps<LeadCalculatorRequest, LeadCalculatorResponse> = SmeLoanLeadFormProps<
		LeadCalculatorRequest,
		LeadCalculatorResponse
	>,
	State extends SmeLoanLeadFormState<FormFields> = SmeLoanLeadFormState<FormFields>,
> extends AbstractLeadForm<LeadCalculatorRequest, LeadCalculatorResponse, FormFields, Props, State> {
	// EventParams
	protected readonly productTypes: string = "SME_LOAN";
	protected readonly componentType: EventComponentType = "leadform";
	protected readonly subscriptionTypes: SubscriptionTypes[] = ["CORPORATE_NEWSLETTER"];

	public static defaultProps = {
		outsideTitle: "Érdeklődj szakértőnknél",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "lead-form",
		};
	}

	protected renderSteps(): ReactNode {
		const { title } = this.props;
		const {
			values: { email },
			submitState,
		} = this.state;
		const request = this.getCalculatorRequest();

		return (
			<>
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					title={title}
					withLoanAmount={!!request?.loanAmount}
					submitState={submitState}
				/>
				<Step.SuccessPage
					stepName="success-page"
					userEmail={email}
					subscriptionTypes={this.subscriptionTypes}
				/>
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "success-page":
				this.onSuccessPage();
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	@autobind
	protected onSuccessPage(): void {
		this.sendLead()
			.then((_response) => {
				this.setState({ submitState: "done" });

				super.onStep("success-page", false);

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	protected getAutoresponderName(): string {
		return "autoresponder-sme-loan";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { lastName, firstName },
		} = this.state;

		return {
			lastName,
			firstName,
		};
	}

	protected getLeadData(): LeadRequest<LeadCalculatorRequest, LeadCalculatorResponse> {
		const {
			values: { companyName },
		} = this.state;
		const data = super.getLeadData();
		data.request.companyName = companyName;

		return data;
	}

	protected getClassName(): string {
		return `${super.getClassName()} sme-loan`;
	}

	protected getProductType(): ProductType {
		return "SME_LOAN";
	}

	protected getComponentName(): string {
		return "SmeLoanLeadForm";
	}
}

export default SmeLoanLeadForm;
